import BaseController from "niteroi/packages/system/controllers/Base.controller";
import ResponsivePopover from "sap/m/ResponsivePopover";
import Fragment from "sap/ui/core/Fragment";
import {ShellBar$AvatarPressedEvent, ShellBar$ProductSwitcherPressedEvent} from "sap/f/ShellBar";
import Menu from "sap/m/Menu";
import {MenuItem$PressEvent} from "sap/m/MenuItem";
import MessageBox from "sap/m/MessageBox";
import {SharedServices} from "niteroi/services/SharedServices";
import UIComponent from "sap/ui/core/UIComponent";
import SideNavigation from "sap/tnt/SideNavigation";
import {ProductSwitch$ChangeEvent} from "sap/f/ProductSwitch";
import {NavigationListItem$SelectEvent} from "sap/tnt/NavigationListItem";
import Input from "sap/m/Input";
import Button from "sap/m/Button";
import JSONModel from "sap/ui/model/json/JSONModel";
import Validation from "niteroi/packages/system/helpers/Validation";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

/**
 * Shell Controller
 * Minimal controller for root shell view
 * Main logic is handled by app component
 *
 * @namespace niteroi.controllers
 */
export default class ShellController extends BaseController {
    private avatarMenuFragment: Menu;
    private productSwitcherFragment: ResponsivePopover;
    private loginPopoverFragment: ResponsivePopover;
    private emailInput: null | Input = null;
    private passwordInput: null | Input = null;
    private loginButton: null | Button = null;

    /**
     * Controller initialization
     */
    public onInit(): void {
        super.onInit();
        this.emailInput = this.byId("worksLoginInputEmail") as Input;
        this.passwordInput = this.byId("worksLoginInputPassword") as Input;
        this.loginButton = this.byId("worksLoginButton") as Button;

        this.getView().setBusyIndicatorDelay(0).setBusy(true);

        const sessionModel = this.getModel("session");
        const settingsModel = this.getModel("settings");
        settingsModel.loadData("/api/settings", "", true, "GET", true, undefined, this.getRequestHeaders()).then(() => {
            const sessionId = window.localStorage.getItem("sessionId");
            if (sessionId) {
                sessionModel.loadData(`/api/sessions/${sessionId}`, "", true, "GET", true, undefined, this.getRequestHeaders()).then().catch().finally();
            }
        }).catch().finally(() => {
            this.getView().setBusy(false).setBusyIndicatorDelay(1000);
        });
    }

    public onPressMenuButton(): void {
        const sideNavigation = this.byId("niteroiSideNavigation") as SideNavigation;
        sideNavigation.setExpanded(!sideNavigation.getExpanded());
    }

    public onSelectSideNavigationListItem(event: NavigationListItem$SelectEvent): void {
        const item = event.getParameter("item");
        const key = item.getKey();

        const bindingContext = item.getBindingContext("session");
        const url = bindingContext.getProperty("url") as string;
        if (url) {
            const parts = url.split("/");
            if (parts.length === 2) {
                this.navTo(parts[0], {
                    "target*": parts[1]
                }, false);
            } else {
                this.navTo(url, {}, false);
            }
        } else {
            this.navTo(key, {}, false);
        }
    }

    public onChangeProductSwitch(event: ProductSwitch$ChangeEvent): void {
        const item = event.getParameter("itemPressed");
        const parts = item.getTargetSrc().split("/");
        if (parts.length === 2) {
            this.navTo(parts[0], {
                "target*": parts[1]
            }, false);
        } else {
            this.navTo(item.getTargetSrc(), {}, false);
        }
    }

    public onPressHomeIcon(): void {
        if (this.getModel("session")?.getProperty("/session/sessionId")) {
            this.navTo("home", {}, false);
        } else {
            this.navTo("start", {}, false);
        }
    }

    public onPressShellBarLogout(): void {
        MessageBox.confirm(this.getText("logoutMessageConfirmation"), {
            onClose: (action: string) => {
                if (action === MessageBox.Action.OK) {
                    window.localStorage.removeItem("sessionId");
                    this.navTo("start", {}, false);
                    const sessionId = this.getModel("session").getProperty("/session/sessionId") as string;
                    const requestModel = this.createRequestModel();
                    requestModel.loadData("/api/auth", {sessionId: sessionId}, true, "DELETE", false, true, this.getRequestHeaders()).then(() => {
                        this.getModel("session").setData({});
                        SharedServices.updateTheme(this.getOwnerComponent() as UIComponent);
                        SharedServices.initializeLanguage(this.getOwnerComponent() as UIComponent);
                    });
                }
            }
        });
    }

    public onPressAvatarMenuItem(event: MenuItem$PressEvent): void {
        const menuItemKey = (event.getSource()).getKey();
        if (menuItemKey === "logout") {
            this.onPressShellBarLogout();
            return;
        } else if (menuItemKey === "profile") {
            const userId = this.getModel("session").getProperty("/user/userId") as string;
            if (userId) {
                this.navTo(`system/users/edit/${userId}`, {id: userId}, false);
            }
            return;
        }
        MessageBox.information("Avatar menu item pressed");
    }

    public async onPressAvatar(event: ShellBar$AvatarPressedEvent): Promise<void> {
        const avatarBtn = event.getParameter("avatar");
        const avatarSrc = avatarBtn.getSrc();
        const sessionId = this.getModel("session").getProperty("/session/sessionId") as string;
        if (avatarSrc === "sap-icon://visits" && !sessionId) {
            if (!this.loginPopoverFragment) {
                this.loginPopoverFragment = this.byId("worksSystemFragmentLogin") as ResponsivePopover;
            }
            this.loginPopoverFragment.openBy(avatarBtn);
        } else {
            if (!this.avatarMenuFragment) {
                this.avatarMenuFragment = await Fragment.load({
                    id: this.getView().getId(),
                    name: "niteroi.packages.system.fragments.AvatarMenu",
                    controller: this
                }) as Menu;

                this.getView().addDependent(this.avatarMenuFragment);
            }
            this.avatarMenuFragment.openBy(avatarBtn);
        }
    }

    public async onPressProductSwitcher(event: ShellBar$ProductSwitcherPressedEvent): Promise<void> {
        if (!this.productSwitcherFragment) {
            this.productSwitcherFragment = await Fragment.load({
                id: this.getView().getId(),
                name: "niteroi.packages.system.fragments.ProductSwitcherPopover",
                controller: this
            }) as ResponsivePopover;

            this.getView().addDependent(this.productSwitcherFragment);
        }
        this.productSwitcherFragment.openBy(event.getParameter("button"));
    }

    public onChangeLoginInput(): void {
        const emailValue = this.emailInput.getValue();
        if (!emailValue || Validation.isValidEmail(emailValue) === false) {
            this.emailInput.setValueState("Error");
            if (!emailValue) {
                this.emailInput.setShowValueStateMessage(true);
            }
        } else {
            this.emailInput.setValueState("None");
            this.emailInput.setShowValueStateMessage(false);
        }

        const passwordValue: string = this.passwordInput.getValue();
        if (passwordValue) {
            this.passwordInput.setValueState("None");
            this.passwordInput.setShowValueStateMessage(false);
        }
    }

    public onSubmitLogin(): void {
        if (!this.passwordInput.getValue()) {
            this.passwordInput.setValueState("Error");
            this.passwordInput.setShowValueStateMessage(true);
            return;
        }
        const emailValue = this.emailInput.getValue();
        if (!emailValue) {
            this.emailInput.setValueState("Error");
            this.emailInput.setShowValueStateMessage(true);
            return;
        }
        if (Validation.isValidEmail(emailValue) === false) {
            this.emailInput.setValueState("Error");
            this.emailInput.setShowValueStateMessage(false);
            return;
        }
        this.doLogin();
    }

    protected doLogin(): void {
        this.getView().setBusyIndicatorDelay(0).setBusy(true);
        const loginData = {
            "email": this.emailInput.getValue(),
            "password": this.passwordInput.getValue()
        };

        this.emailInput.setValue("");
        this.passwordInput.setValue("");

        const sessionModel = this.getModel("session");
        const url = "/api/auth";
        const credentials = JSON.stringify(loginData);
        sessionModel.loadData(url, credentials, true, "POST", false, undefined, this.getRequestHeaders()).then(() => {
            if (sessionModel.getProperty("/sessionId")) {
                window.localStorage.setItem("sessionId", sessionModel.getProperty("/sessionId"));
            }
            SharedServices.updateTheme(this.getOwnerComponent() as UIComponent);
            SharedServices.updateLanguage(sessionModel.getProperty("/user/locale"), this.getOwnerComponent() as UIComponent);
            if (sessionModel.getProperty("/company/packages/0")) {
                const packageKey = sessionModel.getProperty("/company/packages/0/packageKey") as string;
                const packageHref = sessionModel.getProperty("/company/packages/0/packageHref") as string;
                if (packageKey && packageHref) {
                    this.navTo(`${packageKey}/${packageHref}`, undefined, false);
                }
            } else {
                this.setModel(new JSONModel({"item": "system"}), "activeSideNavigationItem");
                this.setModel(new JSONModel({"key": "launchpad"}), "selectedSideNavigationKey");
                this.navTo("system/launchpad", {}, false);
            }
        }).catch((response: ILoadDataResponse) => {
            window.localStorage.removeItem("sessionId");
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false).setBusyIndicatorDelay(1000);
        });
    }
}
